<template>
  <div
    class="box-border w-full h-[66px] shrink-0 flex flex-row items-center px-[5px] bg-[#f5f5f5ff] hover:bg-[#eaeaea] transition-colors cursor-pointer rounded-[14px] relative group"
    @click="emitView"
  >
    <div class="flex-[1.5] min-w-0 px-[5px] text-[12.976px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left flex items-center gap-[6px]">
      <div
        v-if="isEditingEmail"
        class="flex items-center gap-[4px] w-full"
        @click.stop
      >
        <input
          ref="emailInputRef"
          v-model="editEmailValue"
          type="email"
          class="border rounded px-1 text-sm w-full outline-none focus:ring-1 focus:ring-[#043f42]"
          @keyup.enter="saveEmail"
          @keyup.esc="cancelEditEmail"
        >
        <button
          class="text-[#01d680] hover:text-[#043f42] shrink-0"
          title="Save"
          @click="saveEmail"
        >
          <svg
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-4 h-4"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M4.5 12.75l6 6 9-13.5"
          /></svg>
        </button>
        <button
          class="text-red-500 hover:text-red-700 shrink-0"
          title="Cancel"
          @click="cancelEditEmail"
        >
          <svg
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-4 h-4"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 18L18 6M6 6l12 12"
          /></svg>
        </button>
      </div>
      <template v-else>
        <span
          class="truncate cursor-pointer hover:text-[#01d680] transition-colors"
          :title="enrollment.student_email"
          @click.stop="startEditEmail"
        >{{ truncatedEmail }}</span>
        <div
          class="box-border w-[12px] h-[12px] shrink-0 text-[#888888ff] hover:text-[#043f42] cursor-pointer"
          title="Edit Email"
          @click.stop="startEditEmail"
        >
          <svg
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-full h-full"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
            />
          </svg>
        </div>
      </template>
    </div>
    <div class="flex-[1.5] min-w-0 px-[5px] text-[12.976px]/[19px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left truncate">
      {{ studentFullName }}
    </div>
    <div class="flex-[1.2] min-w-0 px-[5px] flex justify-start items-center">
      <div :class="['box-border w-fit h-fit flex flex-row gap-[5.053px] p-[1.895px_3.789px] justify-center items-center rounded-full', statusBgClass]">
        <div :class="['box-border w-[5.684px] shrink-0 h-[5.684px] rounded-full overflow-hidden', statusDotClass]" />
        <div class="text-[8.195px]/[12px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left [white-space:nowrap]">
          {{ statusText }}
        </div>
      </div>
    </div>
    <div class="flex-[1.2] min-w-0 px-[5px] flex flex-col justify-center items-start text-[12.976px]/[16px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left truncate">
      <span class="text-[11px] text-[#5b5c60ff]">{{ dateLabel }}</span>
      <span>{{ dateValue }}</span>
    </div>
    <div class="flex-[1] min-w-0 px-[5px] flex flex-row justify-start items-center">
      <button
        v-if="attachmentsList.length > 0"
        type="button"
        class="box-border w-fit h-fit shrink-0 flex items-center gap-1.5 cursor-pointer overflow-hidden text-[#02949b] hover:text-[#01d680] transition-colors border-none bg-transparent p-0 text-left font-semibold text-[12px] group"
        :title="`View ${attachmentsList.length} attachment${attachmentsList.length > 1 ? 's' : ''}`"
        @click.stop="emitView('attachments')"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="shrink-0 text-[#02949b] group-hover:text-[#01d680] transition-colors"
        >
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
        <span class="underline underline-offset-2">{{ attachmentsList.length }} attachment{{ attachmentsList.length > 1 ? 's' : '' }}</span>
      </button>
      <div
        v-else
        class="text-[12.976px]/[19px] box-border text-[#888888ff] font-[Raleway,system-ui,sans-serif] font-normal text-left [white-space:nowrap] cursor-default opacity-60"
      >
        No attachments
      </div>
    </div>
    <div
      class="flex-[1] min-w-0 px-[5px] flex flex-row gap-[10px] justify-center items-center"
      @click.stop
    >
      <div
        class="box-border w-[14px] h-[14px] shrink-0 overflow-hidden relative cursor-pointer hover:opacity-75"
        title="Resend"
        @click="resend"
      >
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="w-[14px] h-[14px] text-black"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
          />
        </svg>
      </div>
      <div
        class="box-border w-[14px] h-[14px] shrink-0 overflow-hidden relative"
        :class="canDownload ? 'cursor-pointer hover:opacity-75' : 'opacity-30 cursor-not-allowed'"
        :title="canDownload ? 'Download Completed Documents ZIP' : 'Download available once all parties (Training Provider, Learner, and Sponsor) sign'"
        @click="canDownload ? emitDownload() : showDownloadDisabledToast()"
      >
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="w-[14px] h-[14px] text-black"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
          />
        </svg>
      </div>
      <div
        class="box-border w-[14px] h-[14px] shrink-0 relative flex items-center justify-center"
        :class="canDelete ? 'cursor-pointer hover:text-red-600 hover:opacity-75' : 'opacity-30 cursor-not-allowed text-black'"
        :title="canDelete ? 'Delete' : 'Cannot delete once sent'"
        @click="canDelete ? emitDelete() : null"
      >
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-[14px] h-[14px]"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
          />
        </svg>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="isResendModalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      >
        <div class="bg-white rounded-[20px] p-6 max-w-sm w-full shadow-2xl">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            Resend Invitation
          </h3>
          <p class="text-sm text-gray-600 mb-6">
            Are you sure you want to resend the signing invitation? This will dispatch a new email to the learner.
          </p>
          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              @click="closeResendModal"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-[#043f42] hover:bg-[#032e30] rounded-full transition-colors"
              @click="confirmResend"
            >
              Resend Email
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from 'vue'

const props = defineProps({
  enrollment: {
    type: Object,
    required: true
  }
})

const isResendModalOpen = ref(false)
const emailInputRef = ref(null)

const truncatedEmail = computed(() => {
  const email = props.enrollment.student_email || ''
  if (email.length > 20) {
    return email.substring(0, 20) + '...'
  }
  return email
})

const studentFullName = computed(() => {
  return `${props.enrollment.name || ''} ${props.enrollment.surname || ''}`.trim() || 'Unknown'
})

const isEditingEmail = ref(false)
const editEmailValue = ref('')

const studentSubmitter = computed(() => {
  const submission = props.enrollment.submission
  if (!submission) return null
  const email = props.enrollment.student_email || ''
  const submitters = submission?.submitters || []
  return submitters.find(s => s.uuid?.toLowerCase() === 'student') ||
         submitters.find(s => {
           const isSponsor = s.uuid?.toLowerCase().includes('sponsor') || s.name?.toLowerCase().includes('sponsor')
           const isTrainer = s.uuid?.toLowerCase().includes('trainer') || s.name?.toLowerCase().includes('trainer')
           return !isSponsor && !isTrainer && s.email === email
         }) || submitters[0]
})

const canDelete = computed(() => {
  return statusText.value !== 'Completed' && statusText.value !== 'Declined'
})

const canDownload = computed(() => {
  const submitters = props.enrollment.submission?.submitters || []
  if (submitters.length === 0) return false
  return submitters.every(s => !!s.completed_at)
})

const showDownloadDisabledToast = () => {
  const event = new CustomEvent('programme-action', {
    detail: {
      action: 'toast',
      message: 'Download will be available once all parties (Training Provider, Learner, and Sponsor) have signed.',
      type: 'info'
    },
    bubbles: true,
    composed: true
  })
  window.dispatchEvent(event)
}

const statusText = computed(() => {
  if (studentSubmitter.value) {
    const deliveryStatus = studentSubmitter.value.metadata?.email_delivery_status
    if (deliveryStatus === 'complained' || studentSubmitter.value.metadata?.complained_at) return 'Spam Complaint'
    if (deliveryStatus === 'bounced' || studentSubmitter.value.metadata?.bounced_at) return 'Email Bounced'
    if (studentSubmitter.value.declined_at) return 'Declined'
    if (studentSubmitter.value.completed_at) return 'Completed'
    if (studentSubmitter.value.started_at) return 'In progress'
    if (studentSubmitter.value.sent_at) {
      if (studentSubmitter.value.started_at) return 'In progress'
      if (studentSubmitter.value.opened_at) return 'Opened'
      if (deliveryStatus === 'delivered' || studentSubmitter.value.metadata?.delivered_at) return 'Delivered'
      return 'Sent'
    }
    return 'Not sent'
  }
  return 'Not sent'
})

const statusBgClass = computed(() => {
  const text = statusText.value
  if (text === 'Completed') return 'bg-[#01d680ff]'
  if (text === 'Declined') return 'bg-[#fee2e2]'
  if (text === 'In progress') return 'bg-[#fed7aa]'
  if (text === 'Opened') return 'bg-[#bae6fd]'
  if (text === 'Delivered') return 'bg-[#f3e8ff]'
  if (text === 'Sent') return 'bg-[#e2e8f0]'
  if (text === 'Email Bounced') return 'bg-[#fee2e2]'
  if (text === 'Spam Complaint') return 'bg-[#ffedd5]'
  return 'bg-[#f3f4f6]' // 'Not sent'
})

const statusDotClass = computed(() => {
  const text = statusText.value
  if (text === 'Completed') return 'bg-[#043f42ff]'
  if (text === 'Declined') return 'bg-[#dc2626]'
  if (text === 'In progress') return 'bg-[#ea580c]'
  if (text === 'Opened') return 'bg-[#0284c7]'
  if (text === 'Delivered') return 'bg-[#9333ea]'
  if (text === 'Sent') return 'bg-[#64748b]'
  if (text === 'Email Bounced') return 'bg-[#dc2626]'
  if (text === 'Spam Complaint') return 'bg-[#c2410c]'
  return 'bg-[#9ca3af]' // 'Not sent'
})
const dateLabel = computed(() => {
  let label = 'Added on'
  if (studentSubmitter.value) {
    const deliveryStatus = studentSubmitter.value.metadata?.email_delivery_status
    if (deliveryStatus === 'complained') label = 'Complained on'
    else if (deliveryStatus === 'bounced') label = 'Bounced on'
    else if (studentSubmitter.value.declined_at) label = 'Declined on'
    else if (studentSubmitter.value.completed_at) label = 'Completed on'
    else if (studentSubmitter.value.started_at) label = 'Active on'
    else if (studentSubmitter.value.opened_at) label = 'Opened on'
    else if (deliveryStatus === 'delivered') label = 'Delivered on'
    else if (studentSubmitter.value.sent_at) label = 'Sent on'
  }
  return label
})

const dateValue = computed(() => {
  let dateToFormat = props.enrollment.created_at
  if (studentSubmitter.value) {
    if (studentSubmitter.value.declined_at) dateToFormat = studentSubmitter.value.declined_at
    else if (studentSubmitter.value.completed_at) dateToFormat = studentSubmitter.value.completed_at
    else if (studentSubmitter.value.started_at) dateToFormat = studentSubmitter.value.started_at
    else if (studentSubmitter.value.opened_at) dateToFormat = studentSubmitter.value.opened_at
    else if (studentSubmitter.value.sent_at) dateToFormat = studentSubmitter.value.sent_at
  }
  if (!dateToFormat) return 'N/A'
  const d = new Date(dateToFormat)
  return d.toLocaleDateString('en-GB')
})

const attachmentsList = computed(() => {
  if (Array.isArray(props.enrollment.student_uploaded_documents)) {
    return props.enrollment.student_uploaded_documents
  }
  const docs = props.enrollment.submission?.uploaded_documents || []
  if (!Array.isArray(docs)) return []
  const studentId = studentSubmitter.value?.id
  const studentUuid = studentSubmitter.value?.uuid || 'student'
  return docs.filter(doc => {
    if (doc.submitter_id && studentId) return doc.submitter_id === studentId
    if (doc.submitter_uuid) return doc.submitter_uuid === studentUuid || doc.submitter_uuid.toLowerCase() === 'student'
    return false
  })
})

const emitView = (tab = 'main') => {
  const event = new CustomEvent('programme-action', {
    detail: {
      action: 'view_submission',
      enrollment_id: props.enrollment.id,
      submitter_id: studentSubmitter.value?.id,
      tab
    },
    bubbles: true,
    composed: true
  })
  window.dispatchEvent(event)
}

const emitDelete = () => {
  const event = new CustomEvent('programme-action', {
    detail: { action: 'delete', enrollment_id: props.enrollment.id },
    bubbles: true,
    composed: true
  })
  window.dispatchEvent(event)
}

const emitDownload = () => {
  const event = new CustomEvent('programme-action', {
    detail: { action: 'download', enrollment_id: props.enrollment.id },
    bubbles: true,
    composed: true
  })
  window.dispatchEvent(event)
}

const startEditEmail = () => {
  editEmailValue.value = props.enrollment.student_email || ''
  isEditingEmail.value = true
  nextTick(() => {
    emailInputRef.value?.focus()
  })
}

const cancelEditEmail = () => {
  isEditingEmail.value = false
}

const saveEmail = async () => {
  if (!editEmailValue.value || editEmailValue.value.trim() === '') return

  try {
    const newEmail = editEmailValue.value
    const event = new CustomEvent('programme-action', {
      detail: { action: 'update_email', enrollment_id: props.enrollment.id, email: newEmail },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)

    // Optimistically update the submitter if one exists so we don't lose its reference
    if (studentSubmitter.value) {
      studentSubmitter.value.email = newEmail
    }

    // Optimistically update the UI while waiting for server
    props.enrollment.student_email = newEmail
    isEditingEmail.value = false
  } catch (error) {
    console.error(error)
  }
}

const resend = () => {
  if (!props.enrollment.submission?.id) return
  isResendModalOpen.value = true
}

const closeResendModal = () => {
  isResendModalOpen.value = false
}

const confirmResend = async () => {
  isResendModalOpen.value = false

  if (!studentSubmitter.value?.id) {
    const event = new CustomEvent('programme-action', {
      detail: { action: 'toast', message: 'Submitter ID not found.', type: 'error' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
    return
  }

  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    const res = await fetch(`/submissions/${props.enrollment.submission.id}/resend_email`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      // send the submitter id to ensure we resend to the correct student submitter
      body: JSON.stringify({ submitter_id: studentSubmitter.value.id })
    })
    if (res.ok) {
      const event = new CustomEvent('programme-action', {
        detail: { action: 'toast', message: 'Invitation resent successfully', type: 'success' },
        bubbles: true,
        composed: true
      })
      window.dispatchEvent(event)
    } else {
      const event = new CustomEvent('programme-action', {
        detail: { action: 'toast', message: 'Failed to resend email.', type: 'error' },
        bubbles: true,
        composed: true
      })
      window.dispatchEvent(event)
    }
  } catch (error) {
    console.error(error)
    const event = new CustomEvent('programme-action', {
      detail: { action: 'toast', message: 'An error occurred while resending the email.', type: 'error' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
  }
}
</script>
